import { CustomerOrdersTable } from "../orders/customer-orders-table";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { useState } from "react";
import { EditOrderForm } from "../orders/edit-order-form";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Plus } from "lucide-react";
import { CreateOrderForm } from "../orders/create-order-form";
import { BulkUploadForm } from "../orders/bulk-upload-form";
import { useIsMobile } from "@/hooks/use-mobile";
import { useNavigate } from "react-router";
import { useCustomerOrders } from "@/api/use-order";
import { useSession } from "@/services/auth0/session";
import { PaymentStyle } from "@/api.types";
import { usePaymentMethods } from "@/api/use-payment-methods-api";
import { useSidebar } from "@/components/ui/sidebar";

export default function Dashboard() {
  const [isEditSheetOpen, setIsEditSheetOpen] = useState(false);
  const [selectedOrderId, setSelectedOrderId] = useState<string | null>(null);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);

  const isMobile = useIsMobile();
  const { state: sidebarState } = useSidebar();
  const navigate = useNavigate();
  const { customer } = useSession();
  const { data: ordersResponse } = useCustomerOrders({ page: 1, limit: 1 });
  const { data: paymentMethods } = usePaymentMethods();

  const totalOrders = ordersResponse?.metadata.totalCount ?? 0;
  const hasNoPaymentMethods = paymentMethods?.paymentMethods.length === 0;

  // Check if billing setup is required (only if not using manual payments and no payment methods added)
  const requiresBillingSetup =
    customer?.billing?.paymentStyle !== PaymentStyle.MANUAL &&
    hasNoPaymentMethods;

  // Allow order creation if it's the first order or billing is properly set up
  const canCreateOrder = totalOrders === 0 || !requiresBillingSetup;

  const handleEditOrder = async (orderId: string) => {
    if (isMobile) {
      await navigate(`/orders/edit/${orderId}`);
    } else {
      setSelectedOrderId(orderId);
      setIsEditSheetOpen(true);
    }
  };

  const handleCreateClick = async () => {
    if (isMobile) {
      await navigate("/orders/create");
    } else {
      setIsCreateDialogOpen(true);
    }
  };

  return (
    <div className="p-2 sm:p-4">
      {/* Orders table */}
      <CustomerOrdersTable onEditOrder={handleEditOrder} />

      {/* Add Order button - fixed position */}
      <Button
        onClick={handleCreateClick}
        disabled={!canCreateOrder}
        className={`
          fixed bottom-8 z-10
          ${isMobile ? "right-4" : "left-1/2 -translate-x-1/2"}
          ${!isMobile && sidebarState === "expanded" ? "sm:left-[calc(50%+190px)]" : ""}
          rounded-lg w-12 h-12 sm:w-40 sm:h-14 
          shadow-xl 
          bg-blue-900 hover:bg-blue-800 disabled:bg-gray-400 
          sm:flex sm:gap-2 sm:items-center sm:justify-center
        `}
      >
        <Plus className="h-5 w-5 sm:h-6 sm:w-6" />
        <span className="hidden sm:inline text-sm font-medium">Add Order</span>
      </Button>

      {/* Create Order Dialog */}
      <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
        <DialogContent className="max-w-7xl max-h-[95vh] overflow-y-auto p-0 w-[95vw] sm:w-auto">
          <DialogHeader>
            <DialogTitle className="p-6 pb-0">Create New Order</DialogTitle>
          </DialogHeader>

          <Tabs defaultValue="create" className="w-full">
            <TabsList className="grid grid-cols-2 m-6 mt-0">
              <TabsTrigger value="create">Create Order</TabsTrigger>
              <TabsTrigger value="bulk">Bulk Upload</TabsTrigger>
            </TabsList>

            <TabsContent value="create" className="min-w-[1000px]">
              <CreateOrderForm onSuccess={() => setIsCreateDialogOpen(false)} />
            </TabsContent>

            <TabsContent value="bulk" className="min-w-[1000px]">
              <BulkUploadForm />
            </TabsContent>
          </Tabs>
        </DialogContent>
      </Dialog>

      {/* Edit Order Sheet */}
      <Sheet open={isEditSheetOpen} onOpenChange={setIsEditSheetOpen}>
        <SheetContent className="w-full sm:max-w-[900px] overflow-y-auto">
          <SheetHeader>
            <SheetTitle className="mb-4">Edit Order</SheetTitle>
          </SheetHeader>

          {selectedOrderId && (
            <EditOrderForm
              orderId={selectedOrderId}
              onSuccess={() => setIsEditSheetOpen(false)}
            />
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
